/**
 * Decode a JWT payload **without** verifying the signature.
 *
 * Verification lives on the backend (`packages/auth/decode_supabase_jwt`);
 * browser-side reads are only used to drive UI (display the org banner,
 * tag Sentry, decide whether to pre-emptively refresh). Returns `null` on
 * any malformed input rather than throwing — callers cascade through this
 * for display logic and the alternative is a white screen.
 *
 * Handles base64url (`-` / `_`) → base64 (`+` / `/`), pads to a multiple
 * of 4, and round-trips through `TextDecoder` so multi-byte UTF-8 chars
 * (display names, etc.) survive `atob`'s Latin-1 contract.
 */
export function decodeUnverifiedJwt<T = Record<string, unknown>>(
  token: string | null | undefined
): T | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}

/**
 * Check whether `token`'s `exp` claim has passed (or is within `bufferSeconds`).
 *
 * Treats missing/unreadable tokens as expired so callers default to "refresh
 * or sign out" rather than "proceed with stale state". Buffer defaults to
 * 60s, which matches the dashboard's pre-emptive-refresh window.
 */
export function isJwtExpired(
  token: string | null | undefined,
  bufferSeconds: number = 60
): boolean {
  const payload = decodeUnverifiedJwt<{ exp?: number }>(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  const expirationMs = payload.exp * 1000;
  return Date.now() + bufferSeconds * 1000 >= expirationMs;
}
